/*
=============================================================================
FILE: custom.js
PURPOSE:
  Complete JavaScript enhancement system for APO form initialization and interaction
  Contains 5 independent systems: Text Limiter, Prescription Form Detection,
  Form Step Detection, Radio/Checkbox Styling, and Image Layout Optimization

LOCATION: Shopify product page (APO form area)
APPLIES TO:
  - Text descriptions in variant options (15-word limit with toggle)
  - Prescription form field detection (OD, OS, Cylinder, Axis, PD, ADD)
  - Form step detection (card-only vs. form input)
  - Radio/checkbox card clickability enhancement
  - Image layout positioning (left/right variants)

SYSTEMS OVERVIEW:
  1. TEXT LIMITER (82 lines)
     - Truncates descriptions to 15 words
     - Creates "Read More/Less" toggle button
     - Stores original text in data-original-text attribute
     - Targets: .mw-option__value .mw-stack__item

  2. PRESCRIPTION FORM DETECTION (160 lines)
     - Pattern-based field detection (label text matching)
     - Adds .prescription-3col, .prescription-full-width classes
     - Detects OD, OS, Cylinder, Axis, PD, ADD, Guide, Upload
     - Creates visual 3-column layout for compatible fields
     - Adds data-field-type attribute for reference

  3. FORM STEP DETECTION (90 lines)
     - Detects form layout styles (card vs. form input)
     - Card style: 350px fixed height
     - Form input style: auto height, padding-based
     - Applies data-form-step attribute
     - Helps JavaScript systems adapt to form variant

  4. RADIO/CHECKBOX STYLING (75 lines)
     - Makes full card clickable (non-intrusive approach)
     - Adds pointer-events override system
     - Creates clickable area around radio/checkbox
     - Handles focus and hover states
     - Maintains accessibility with proper focus handling

  5. IMAGE LAYOUT OPTIMIZER (140 lines)
     - Detects image positioning patterns
     - Generates dynamic CSS for image sizes
     - Creates preset configurations for images
     - Applies left/right positioning to variant cards
     - Supports multiple image layout modes

INITIALIZATION:
  - Retry logic: 0ms → 1000ms → 3000ms attempts
  - MutationObserver for dynamic DOM monitoring (300ms debounced)
  - Public API: window.combinedSystem object
  - Auto-initialization on page load

KEY FEATURES:
  - No external framework dependencies (vanilla JavaScript)
  - Non-intrusive styling (CSS-only where possible)
  - Debounced mutation monitoring for performance
  - Fallback retry mechanism for slow form loading
  - Data attributes for tracking and debugging

=============================================================================
*/

/* Combined Universal System - Text Limiter + Prescription Forms + Layout Enhancement */
(function() {
    'use strict';
    
    // ===== UTILITY: DEBOUNCE FUNCTION =====
    // Prevents excessive function calls during rapid DOM changes
    // wait: milliseconds to delay execution

    function debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            clearTimeout(timeout);
            timeout = setTimeout(() => func(...args), wait);
        };
    }
    
    const CONFIG = {
        // System 1 (Text Limiter) configuration
        wordLimit: 15,                          // Maximum words before truncation
        readMoreText: 'Read More',              // Toggle button text to expand
        readLessText: 'Read Less',              // Toggle button text to collapse
        animationDuration: 300,                 // Fade animation ms
        // Selector for text elements to process (option descriptions)
        targetSelector: '.mw-option__value .mw-stack__item',
        // Selectors to exclude from text processing
        excludeSelectors: ['.mw-option__value-title', '.mw-option__media', 'label', 'input']
    };
    
    // ===== SYSTEM 1: TEXT LIMITER UTILITY FUNCTIONS =====
    // Implements 15-word limit with "Read More/Less" toggle button
    
    // Count words in a text string (splits by whitespace)
    function countWords(text) {
        const trimmed = text.trim();
        return trimmed ? trimmed.split(/\s+/).length : 0;
    }
    
    // Truncate text to specified word limit; returns object with truncated text and hasMore flag
    function truncateText(text, wordLimit) {
        const words = text.trim().split(/\s+/);
        return words.length <= wordLimit 
            ? { truncated: text, hasMore: false }
            : { truncated: words.slice(0, wordLimit).join(' '), hasMore: true };
    }
    
    // Create toggle button for expanding/collapsing truncated text
    function createToggleButton(text, onClick) {
        const button = document.createElement('button');
        button.className = 'text-limiter-toggle';
        button.textContent = text;
        button.type = 'button';
        
        // Force override all pointer-events restrictions (APO may block clicks)
        button.setAttribute('data-clickable', 'true');
        
        // Apply inline styling for visibility and interaction
        Object.assign(button.style, {
            background: 'none',
            border: 'none',
            color: '#007cba',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '600',
            textDecoration: 'underline',
            padding: '2px 2px',
            display: 'inline',
            lineHeight: '1',
            outline: 'none',
            transition: 'color 0.2s ease',
            zIndex: '999',
            position: 'relative',
            pointerEvents: 'auto',
            // Force clickability
            webkitUserSelect: 'none',
            mozUserSelect: 'none',
            msUserSelect: 'none',
            userSelect: 'none'
        });
        
        // Apply all critical styles in one operation
        const criticalStyles = 'pointer-events: auto !important; z-index: 999 !important; position: relative !important;';
        button.setAttribute('style', button.getAttribute('style') + '; ' + criticalStyles);
        
        // Hover effects
        button.addEventListener('mouseenter', () => {
            button.style.color = '#005a8a';
        });
        
        button.addEventListener('mouseleave', () => {
            button.style.color = '#007cba';
        });
        
        // Enhanced click handler with multiple event types
        const handleClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            onClick();
        };
        
        button.addEventListener('click', handleClick, true);
        button.addEventListener('mousedown', handleClick, true);
        button.addEventListener('touchstart', handleClick, true);
        
        return button;
    }
    
    // Function to process text content and add read more functionality
    function processTextElement(element) {
        // Skip if already processed
        if (element.dataset.textLimiterProcessed === 'true') {
            return;
        }
        
        // Get the original text content
        const originalText = element.textContent.trim();
        
        // Skip if text is too short
        if (countWords(originalText) <= CONFIG.wordLimit) {
            return;
        }
        
        // Mark as processed
        element.dataset.textLimiterProcessed = 'true';
        
        // Store original text
        element.dataset.originalText = originalText;
        
        // Create truncated version
        const { truncated } = truncateText(originalText, CONFIG.wordLimit);
        
        // Create container for text and button
        const container = document.createElement('div');
        container.className = 'text-limiter-container';
        container.style.display = 'inline';
        
        // Create text span
        const textSpan = document.createElement('span');
        textSpan.className = 'text-limiter-text';
        textSpan.textContent = truncated + '...';
        
        // State tracking
        let isExpanded = false;
        
        // Toggle function
        function toggleText() {
            isExpanded = !isExpanded;
            
            if (isExpanded) {
                // Show full text
                textSpan.textContent = originalText + ' ';
                button.textContent = CONFIG.readLessText;
            } else {
                // Show truncated text
                textSpan.textContent = truncated + '...';
                button.textContent = CONFIG.readMoreText;
            }
        }
        
        // Create toggle button
        const button = createToggleButton(CONFIG.readMoreText, toggleText);
        
        // Clear original element and add new structure
        element.innerHTML = '';
        container.appendChild(textSpan);
        container.appendChild(button);
        element.appendChild(container);
        
        // Optimize pointer-events with batch style application
        const autoPointer = 'pointer-events: auto !important;';
        element.style.pointerEvents = 'auto';
        container.style.pointerEvents = 'auto';
        textSpan.style.pointerEvents = 'none';
        button.style.pointerEvents = 'auto';
        
        element.setAttribute('style', element.getAttribute('style') + '; ' + autoPointer);
        container.setAttribute('style', container.getAttribute('style') + '; ' + autoPointer);
        button.setAttribute('style', button.getAttribute('style') + '; ' + autoPointer + ' z-index: 999 !important;');
    }
    
    // Optimized function to process card descriptions (radio & checkbox)
    function processCardDescriptions() {
        // Combined selector for both radio and checkbox cards
        const allCards = document.querySelectorAll('.mw-groups .mw-option--type-radio .mw-option__value, .mw-groups .mw-option--type-checkbox .mw-option__value');
        
        allCards.forEach(card => {
            const descriptionElements = card.querySelectorAll(CONFIG.targetSelector);
            
            descriptionElements.forEach(element => {
                // Skip excluded elements or those containing inputs/labels
                if (CONFIG.excludeSelectors.some(selector => element.matches(selector) || element.closest(selector)) ||
                    element.querySelector('input, label, .mw-option__value-title')) {
                    return;
                }
                
                // Process only text-heavy elements
                const textContent = element.textContent.trim();
                if (textContent.length > 50 && countWords(textContent) > CONFIG.wordLimit) {
                    processTextElement(element);
                }
            });
        });
    }
    
    // Optimized CSS injection with duplicate prevention
    function addTextLimiterStyles() {
        // Prevent duplicate injection
        if (document.getElementById('text-limiter-styles')) return;
        
        const style = document.createElement('style');
        style.id = 'text-limiter-styles';
        style.textContent = `
            .text-limiter-container {
                display: inline !important;
                line-height: 1.4 !important;
                pointer-events: auto !important;
            }
            
            .text-limiter-text {
                display: inline !important;
                pointer-events: none !important;
            }
            
            .text-limiter-toggle {
                display: inline !important;
                vertical-align: baseline !important;
                white-space: nowrap !important;
                pointer-events: auto !important;
                z-index: 999 !important;
                position: relative !important;
            }
            
            .text-limiter-toggle:focus {
                outline: 2px solid #007cba !important;
                outline-offset: 1px !important;
                border-radius: 2px !important;
            }
            
            /* Force override any card pointer-events restrictions - ONLY mw-groups */
            .mw-groups .mw-option__value .text-limiter-container {
                pointer-events: auto !important;
            }
            
            .mw-groups .mw-option__value .text-limiter-toggle {
                pointer-events: auto !important;
                z-index: 999 !important;
                position: relative !important;
            }
            
            /* Override combined-system.css restrictions - ONLY mw-groups */
            .mw-apo-container .mw-option--type-radio .mw-option__value .text-limiter-container {
                pointer-events: auto !important;
            }
            
            .mw-apo-container .mw-option--type-radio .mw-option__value .text-limiter-toggle {
                pointer-events: auto !important;
                z-index: 999 !important;
                position: relative !important;
            }

            /* Checkbox text limiter support */
            .mw-apo-container .mw-option--type-checkbox .mw-option__value .text-limiter-container {
                pointer-events: auto !important;
            }
            
            .mw-apo-container .mw-option--type-checkbox .mw-option__value .text-limiter-toggle {
                pointer-events: auto !important;
                z-index: 999 !important;
                position: relative !important;
            }
            
            /* Force override for any element with text limiter processed */
            [data-text-limiter-processed="true"] {
                pointer-events: auto !important;
            }
            
            [data-text-limiter-processed="true"] .text-limiter-container {
                pointer-events: auto !important;
            }
            
            [data-text-limiter-processed="true"] .text-limiter-toggle {
                pointer-events: auto !important;
                z-index: 999 !important;
                position: relative !important;
            }
            
            /* Mobile responsiveness */
            @media (max-width: 768px) {
                .text-limiter-toggle {
                    font-size: 12px !important;
                    margin: 2px 0 0 4px !important;
                    display: inline-block !important;
                    pointer-events: auto !important;
                }
            }
            
            @media (max-width: 480px) {
                .text-limiter-toggle {
                    font-size: 11px !important;
                    margin: 4px 0 0 0 !important;
                    display: block !important;
                    width: fit-content !important;
                    pointer-events: auto !important;
                }
            }
        `;
        
        document.head.appendChild(style);
    }
    
    // Function to initialize text limiter system
    function initializeTextLimiter() {
        try {
            addTextLimiterStyles();
            processCardDescriptions();
        } catch (error) {
            // Silent error handling
        }
    }
    
    // Function to reinitialize for dynamic content
    function reinitializeTextLimiter() {
        // Wait for other systems to load first
        setTimeout(() => {
            try {
                processCardDescriptions();
            } catch (error) {
                // Silent error handling
            }
        }, 100);
    }
    
    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializeTextLimiter);
    } else {
        initializeTextLimiter();
    }
    
    // Optimized MutationObserver with debouncing for better performance
    const debouncedReinitialize = debounce(reinitializeTextLimiter, 150);
    
    const observer = new MutationObserver(function(mutations) {
        for (const mutation of mutations) {
            if (mutation.type === 'childList') {
                for (const node of mutation.addedNodes) {
                    if (node.nodeType === 1 && 
                        (node.matches && node.matches('.mw-groups, .mw-option--type-radio, .mw-option--type-checkbox') ||
                         node.querySelector && node.querySelector('.mw-groups'))) {
                        debouncedReinitialize();
                        return; // Early exit on first match
                    }
                }
            }
        }
    });
    
    // Start observing
    observer.observe(document.body, {
        childList: true,
        subtree: true
    });
    
    // Optimized initialization with reduced redundancy
    setTimeout(initializeTextLimiter, 1000);
    
    // Single delayed initialization to fix pointer-events after other systems load
    setTimeout(() => {
        const criticalPointerStyles = '; pointer-events: auto !important; z-index: 999 !important;';
        const autoPointerStyle = '; pointer-events: auto !important;';
        
        // Batch style updates
        document.querySelectorAll('.mw-groups .text-limiter-toggle').forEach(button => {
            button.style.pointerEvents = 'auto';
            button.style.zIndex = '999';
            button.style.position = 'relative';
            button.setAttribute('style', button.getAttribute('style') + criticalPointerStyles);
        });
        
        document.querySelectorAll('.mw-groups [data-text-limiter-processed="true"]').forEach(element => {
            element.style.pointerEvents = 'auto';
            element.setAttribute('style', element.getAttribute('style') + autoPointerStyle);
        });
        
        reinitializeTextLimiter();
    }, 3000);
    
    // Public API for Text Limiter system
    window.textLimiter = {
        process: processCardDescriptions,
        reinitialize: reinitializeTextLimiter,
        setWordLimit: (limit) => {
            CONFIG.wordLimit = limit;
            reinitializeTextLimiter();
        },
        setTexts: (readMore, readLess) => {
            CONFIG.readMoreText = readMore;
            CONFIG.readLessText = readLess;
        },
        config: CONFIG,
        version: '1.0.0'
    };
    
})();

/*
=============================================================================
SYSTEM 2, 3, 4, 5: PRESCRIPTION FORM, FORM STEPS, RADIO/CHECKBOX, IMAGE LAYOUT
=============================================================================
Unified system for APO form detection, styling, and enhancement
- Prescription field detection (pattern-based on labels)
- Form step type detection (card vs. form input)
- Radio/checkbox card enhancement (full card clickability)
- Image layout optimization (dynamic CSS generation)
=============================================================================
*/

(function() {
    'use strict';

    // ===== SELF-CONTAINED DEBOUNCE =====
    // CRITICAL: This must be defined locally inside this IIFE.
    // DO NOT rely on a global or theme-provided debounce — it may not be available
    // when this IIFE executes (load order is not guaranteed).
    // The previous bug: 'debounce is not defined' ReferenceError at the debouncedRerun
    // declaration caused mainObserver to never be set up, so APO re-renders (e.g. 
    // color swatch switches) had zero effect on our system.
    function debounce(func, wait) {
        let timeout;
        return function(...args) {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        };
    }

    // ===== SYSTEM 2: PRESCRIPTION FORM DETECTION & STYLING ====
    
    // Add universal CSS immediately to prevent flash
    const prescriptionStyle = document.createElement('style');
    prescriptionStyle.textContent = `
        /* Force immediate APO container visibility */
        .mw-apo-container {
            visibility: visible !important;
            opacity: 1 !important;
        }
        
        /* Shared radio & checkbox base styling */
        .mw-groups .mw-option--type-radio .mw-option__value,
        .mw-groups .mw-option--type-checkbox .mw-option__value {
            display: flex !important; align-items: stretch !important; flex-direction: row !important;
            padding: 16px 10px !important; margin-bottom: 12px !important; gap: 5px !important;
            border: 2px solid #e5e7eb !important; border-radius: 12px !important;
            background-color: #ffffff !important; cursor: pointer !important;
            transition: all 0.3s ease !important; position: relative !important;
            min-height: 80px !important; box-sizing: border-box !important;
        }
        
        /* Shared hover & selected states */
        .mw-groups .mw-option--type-radio .mw-option__value:hover,
        .mw-groups .mw-option--type-checkbox .mw-option__value:hover,
        .mw-groups .mw-option--type-radio .mw-option__value.mw-option__value--selected,
        .mw-groups .mw-option--type-checkbox .mw-option__value.mw-option__value--selected {
            border-color: #000000 !important; background-color: #f9fafb !important;
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2) !important;
        }
        
        /* Force immediate radio button hiding - ONLY inside mw-groups container */
        .mw-groups .mw-option--type-radio .mw-option__value input[type="radio"] {
            display: none !important;
            width: 0 !important;
            height: 0 !important;
            margin: 0 !important;
            opacity: 0 !important;
            position: absolute !important;
            pointer-events: none !important;
        }
        
        /* Force immediate checkbox positioning - ONLY inside mw-groups container */
        .mw-groups .mw-option--type-checkbox .mw-option__value input[type="checkbox"] {
            display: block !important;
            width: 18px !important;
            height: 18px !important;
            margin: 0 !important;
            position: absolute !important;
            right: 20px !important;
            top: 50% !important;
            transform: translateY(-50%) !important;
            z-index: 10 !important;
        }
        
        .mw-group__progress { display: none !important; }
        .mw-group__progress.show-progress { display: block !important; }
        .mw-group__button-next.disabled { opacity: 0.5 !important; cursor: not-allowed !important; pointer-events: none !important; background-color: #ccc !important; color: #666 !important; }
        .mw-group__options.mw-group__fixed-height { height: auto !important; min-height: auto !important; max-height: none !important; }
        .mw-group__button { margin-top: 20px !important; position: relative !important; clear: both !important; }
        
        /* Only apply fixed height when in prescription multi-step mode and steps are visible */
        .prescription-multistep .mw-group__options.mw-group__fixed-height {
            height: 350px !important;
        }
        
        /* Dynamic exception: ANY step with form fields gets auto height - no scrollbar needed */
        /* This targets steps containing select, text, or file inputs - the actual form step */
        .prescription-multistep .mw-group__options.mw-group__fixed-height:has(.mw-option--type-select),
        .prescription-multistep .mw-group__options.mw-group__fixed-height:has(.mw-option--type-text),
        .prescription-multistep .mw-group__options.mw-group__fixed-height:has(.mw-option--type-file) {
            height: auto !important;
            min-height: auto !important;
            max-height: none !important;
            overflow: visible !important;
        }
        
        /* Fallback: JavaScript-detected form steps (for browsers without :has() support) */
        .prescription-multistep .mw-group__options.form-step-detected {
            height: auto !important;
            min-height: auto !important;
            max-height: none !important;
            overflow: visible !important;
        }
    `;
    document.head.appendChild(prescriptionStyle);
    
    // Configuration - Add keywords to detect different field types
    const FIELD_PATTERNS = {
        threeColumn: [
             'od', 'os', 'right ', 'left eye',
            'cylinder', 'cyclinder', 'cyl',
            'axis', 'axe'
        ],
        fullWidth: [
            'add', 'pd', 'pupil distance', 'guide', 'upload', 
            'file', 'distance', 'measure', 'prescription'
        ]
    };
    
    let currentStep = 0;
    let detectedRadios = null;
    
    // ===== FIX: Swatch-switch stability flags =====
    // Prevents duplicate event listeners from stacking up on every APO re-render.
    // Each color swatch switch causes APO to re-render, triggering runCombinedSystem.
    // Without these flags, universalToggle adds a new 'change' listener every time
    // and createContinuousMonitor starts a new setInterval — causing conflicts.
    let changeListenerAttached = false;
    let continuousMonitorInterval = null;
    
    // Function to wait for elements to be present in DOM
    function waitForElements(selectors, timeout = 10000) {
        return new Promise((resolve, reject) => {
            const startTime = Date.now();
            
            function checkElements() {
                const allFound = selectors.every(selector => {
                    const elements = document.querySelectorAll(selector);
                    return elements.length > 0;
                });
                
                if (allFound) {
                    resolve(true);
                } else if (Date.now() - startTime > timeout) {
                    reject(new Error('Elements not found within timeout'));
                } else {
                    setTimeout(checkElements, 100);
                }
            }
            
            checkElements();
        });
    }
    
    // Optimized pattern matching with cached toLowerCase
    function containsPattern(text, patterns) {
        const lowerText = text.toLowerCase();
        return patterns.some(pattern => lowerText.includes(pattern));
    }
    
    // Function to detect and classify prescription fields
    function detectPrescriptionFields() {
        // Optimized selector to exclude radio/checkbox/button from start
        const customOptions = document.querySelectorAll('.mw-groups .mw-option[data-option-id]:not(.mw-option--type-radio):not(.mw-option--type-checkbox):not(.mw-option--type-button)');
        
        customOptions.forEach(option => {
            const label = option.querySelector('.mw-label .mw-text, .mw-label');
            if (!label) return;
            
            const labelText = label.textContent || label.innerText || '';
            const lowerLabel = labelText.toLowerCase();
            
            // Check if it's a 3-column field (Eye/Cylinder/Axis)
            if (containsPattern(labelText, FIELD_PATTERNS.threeColumn)) {
                option.classList.add('prescription-3col');
                if (lowerLabel.includes('cylinder') || lowerLabel.includes('cyclinder')) {
                    option.classList.add('auto-detected-cylinder-field');
                } else if (lowerLabel.includes('axis')) {
                    option.classList.add('auto-detected-axis-field');
                }
            } else if (containsPattern(labelText, FIELD_PATTERNS.fullWidth)) {
                option.classList.add('prescription-full-width', 'auto-detected-full-width-field');
            }
        });
    }
    
    // Function to apply styling when CSS :has() is not supported
    function applyFallbackStyling() {
        if (!CSS.supports('selector(:has(*))')) {
            const fallbackStyle = document.createElement('style');
            fallbackStyle.textContent = `
                .auto-detected-eye-field,
                .auto-detected-cylinder-field,
                .auto-detected-axis-field,
                .prescription-3col {
                    display: inline-block !important;
                    margin-right: 2% !important;
                    vertical-align: top !important;
                    box-sizing: border-box !important;
                    margin-bottom: 15px !important;
                }
                
                .auto-detected-axis-field {
                    margin-right: 0 !important;
                }
                
                .auto-detected-full-width-field,
                .prescription-full-width {
                    display: block !important;
                    width: 100% !important;
                    margin-bottom: 15px !important;
                    box-sizing: border-box !important;
                    margin-right: 0 !important;
                    clear: both !important;
                }
                
                @media (max-width: 768px) {
                    .auto-detected-eye-field,
                    .auto-detected-cylinder-field,
                    .auto-detected-axis-field,
                    .prescription-3col {
                        width: 100% !important;
                        margin-right: 0 !important;
                        display: block !important;
                    }
                }
            `;
            document.head.appendChild(fallbackStyle);
        }
    }
    
    // ===== SYSTEM 3: FORM STEP DETECTION & AUTO-SIZING =====
    // Detects form steps (card layout vs. form input layout) and applies appropriate styling
    // Card steps: 350px fixed height (card-style options). Form steps: auto height (input fields)
    
    // Function to dynamically detect and mark form steps (for browsers without :has() support)
    function detectAndMarkFormSteps() {
        try {
            // Find all step containers inside prescription multistep mode
            const multistepContainer = document.querySelector('.prescription-multistep');
            if (!multistepContainer) return;
            
            const allSteps = document.querySelectorAll('.prescription-multistep .mw-group');
            
            allSteps.forEach(step => {
                const optionsContainer = step.querySelector('.mw-group__options');
                if (!optionsContainer) return;
                
                // Check if this step contains form fields (select, text, file inputs)
                const hasSelectFields = optionsContainer.querySelector('.mw-option--type-select');
                const hasTextFields = optionsContainer.querySelector('.mw-option--type-text');
                const hasFileFields = optionsContainer.querySelector('.mw-option--type-file');
                
                // If step has form fields, mark it as a form step
                if (hasSelectFields || hasTextFields || hasFileFields) {
                    optionsContainer.classList.add('form-step-detected');
                    
                    // Apply auto height directly via JavaScript (fallback for browsers without :has())
                    if (optionsContainer.classList.contains('mw-group__fixed-height')) {
                        optionsContainer.style.height = 'auto';
                        optionsContainer.style.minHeight = 'auto';
                        optionsContainer.style.maxHeight = 'none';
                        optionsContainer.style.overflow = 'visible';
                    }
                }
            });
        } catch (error) {
            // Silent error handling
        }
    }
    
    // Function to auto-detect radio button structure
    function detectRadioStructure() {
        // ONLY target radios inside .mw-groups container
        const allRadios = document.querySelectorAll('.mw-groups input[type="radio"]');
        const radioGroups = {};
        
        allRadios.forEach(radio => {
            const groupName = radio.name;
            const label = radio.closest('label') || document.querySelector(`.mw-groups label[for="${radio.id}"]`);
            const valueContainer = radio.closest('.mw-option__value');
            
            if (!radioGroups[groupName]) radioGroups[groupName] = [];
            
            let labelText = '';
            if (label) {
                labelText = label.textContent || label.innerText || '';
            }
            
            radioGroups[groupName].push({
                radio: radio,
                id: radio.id,
                value: radio.value,
                label: labelText.toLowerCase().trim(),
                container: valueContainer
            });
        });
        
        // Find the group that contains prescription options
        for (const [groupName, radios] of Object.entries(radioGroups)) {
            const hasNonPrescription = radios.some(r => 
                r.label.includes('non prescription') || 
                r.label.includes('non-prescription') ||
                r.label.includes('no prescription')
            );
            const hasPrescription = radios.some(r => 
                r.label.includes('prescription') && 
                !r.label.includes('non')
            );
            
            if (hasNonPrescription && hasPrescription) {
                detectedRadios = {
                    groupName: groupName,
                    nonPrescription: radios.find(r => 
                        r.label.includes('non prescription') || 
                        r.label.includes('non-prescription') ||
                        r.label.includes('no prescription')
                    ),
                    prescription: radios.find(r => 
                        r.label.includes('prescription') && 
                        !r.label.includes('non')
                    ),
                    atHome: radios.find(r => 
                        r.label.includes('at home') || 
                        r.label.includes('try on')
                    )
                };
                
                return detectedRadios;
            }
        }
        
        return null;
    }
    
    // ===== MODULE-LEVEL updateUI =====
    // CRITICAL FIX: updateUI was previously defined INSIDE universalToggle() as a closure.
    // It captured progressBar/nextButton/mainContainer once when universalToggle() first ran.
    // After APO re-renders (swatch switch), those DOM elements are replaced — the old refs
    // become orphaned. Calling .classList.add() on orphaned elements has ZERO visible effect.
    // Moved here so it always queries fresh DOM elements on every call.
    //
    // Also fixed: prescription was detected by radio element ID (detectedRadios.prescription.id).
    // APO regenerates element IDs on EVERY variant reload, so the ID never matched after a
    // swatch switch, making isPrescription always false even when prescription was selected.
    // Now detects by label text which is stable across re-renders.
    function updateUI() {
        try {
         if (!detectedRadios) {
                console.log('[Sporteyes] updateUI: skipped — detectedRadios is null');
                return;
            }

            // ALWAYS get fresh DOM refs — never use closures for elements APO can replace
            const progressBar = document.querySelector('.mw-group__progress');
            const nextButton = document.querySelector('.mw-group__button-next');
            const mainContainer = document.querySelector('.mw-group__container');

    

            // Find checked radio by group name
            const checkedRadio = document.querySelector(`.mw-groups input[name="${detectedRadios.groupName}"]:checked`);
           

            if (!checkedRadio) {
                nextButton.textContent = 'Add to Cart';
                nextButton.classList.add('disabled');
                progressBar.classList.remove('show-progress');
                if (mainContainer) mainContainer.classList.remove('prescription-multistep');

                return;
            }

            // ===== FIX: Detect prescription by LABEL TEXT, not element ID =====
            // APO regenerates radio IDs on every variant reload (e.g. swatch switch).
            // Matching by ID (detectedRadios.prescription.id) always fails after a re-render.
            // Label text is stable — it's the option NAME set in Shopify admin, not a dynamic ID.
            const card = checkedRadio.closest('.mw-option__value');
            const cardLabelEl = card ? (card.querySelector('.mw-text, label, .mw-label') || card) : null;
            const cardLabelText = cardLabelEl ? cardLabelEl.textContent.toLowerCase().trim() : '';
            const isPrescription = cardLabelText.includes('prescription') &&
                                   !cardLabelText.includes('non') &&
                                   !cardLabelText.includes('at home');



            if (isPrescription) {
                progressBar.classList.add('show-progress');
                nextButton.textContent = 'Next';
                nextButton.classList.remove('disabled');
                if (mainContainer) mainContainer.classList.add('prescription-multistep');

            } else {
                progressBar.classList.remove('show-progress');
                nextButton.textContent = 'Add to Cart';
                nextButton.classList.remove('disabled');
                if (mainContainer) mainContainer.classList.remove('prescription-multistep');
            }
        } catch (e) {
            console.log('[Sporteyes] updateUI ERROR:', e.message, e);
        }
    }

    function universalToggle() {
        try {
            // Auto-detect radio structure if not already done
            if (!detectedRadios) {
                detectedRadios = detectRadioStructure();
                if (!detectedRadios) {
                    console.log('[Sporteyes] universalToggle: detectRadioStructure returned null — no prescription group found');
                    return;
                }
            }

            console.log('[Sporteyes] universalToggle: running with groupName=', detectedRadios.groupName);

            const progressBar = document.querySelector('.mw-group__progress');
            const nextButton = document.querySelector('.mw-group__button-next');

            if (!progressBar || !nextButton) {
                console.log('[Sporteyes] universalToggle: skipped — progressBar or nextButton missing from DOM');
                return;
            }

            // ===== FIX: Only attach the change listener ONCE =====
            // Previously this was added every time universalToggle() ran (on every swatch switch).
            // Multiple listeners all fired simultaneously — the one reading stale detectedRadios
            // (null) threw a silent error that broke the progress bar and button selection.
            if (!changeListenerAttached) {
                changeListenerAttached = true;
                console.log('[Sporteyes] universalToggle: attaching change listener (first time only)');
                document.addEventListener('change', function(e) {
                    // Guard: detectedRadios is set to null during re-render — skip safely
                    if (!detectedRadios) return;
                    if (e.target && e.target.name === detectedRadios.groupName) {
                        console.log('[Sporteyes] change event: radio changed in group', detectedRadios.groupName, '| radio id=', e.target.id);
                        // Only update UI, don't trigger any additional events
                        setTimeout(updateUI, 100);
                        // Visual state updates with longer delay to avoid conflicts
                        setTimeout(() => updateRadioSelectionStates(e.target), 200);
                    }
                }, { passive: true });
            } else {
                console.log('[Sporteyes] universalToggle: change listener already attached — skipping re-attach');
            }

            // Initial run with delay
            setTimeout(updateUI, 200);
            
        } catch (error) {
            console.log('[Sporteyes] universalToggle ERROR:', error.message, error);
        }
    }    // Function to update radio button selection visual states
    function updateRadioSelectionStates(selectedRadio) {
        try {
            // Get all radio buttons in the same group ONLY inside .mw-groups
            const groupName = selectedRadio.name;
            const allRadiosInGroup = document.querySelectorAll(`.mw-groups input[name="${groupName}"]`);
            
            allRadiosInGroup.forEach(radio => {
                const optionValue = radio.closest('.mw-option__value');
                if (optionValue) {
                    if (radio.checked) {
                        // Add selected class and styling
                        optionValue.classList.add('mw-option__value--selected');
                        optionValue.style.borderColor = '#000000';
                        optionValue.style.backgroundColor = '#f9fafb';
                        optionValue.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.2)';
                    } else {
                        // Remove selected class and styling
                        optionValue.classList.remove('mw-option__value--selected');
                        optionValue.style.borderColor = '#e5e7eb';
                        optionValue.style.backgroundColor = '#ffffff';
                        optionValue.style.boxShadow = 'none';
                    }
                }
            });
        } catch (error) {
            // Silent error handling
        }
    }
    
    // ===== SYSTEM 4: RADIO/CHECKBOX CARD CLICKABILITY ENHANCEMENT =====
    // Makes entire radio and checkbox cards clickable (non-intrusive approach)
    // Preserves native radio/checkbox behavior while extending clickable area to full card
    
    // Function to make entire radio card clickable (Non-intrusive approach)
    function makeRadioCardsClickable() {
        try {
            // ONLY target radio cards inside .mw-groups container
            const radioCards = document.querySelectorAll('.mw-groups .mw-option--type-radio .mw-option__value');
            
            radioCards.forEach(card => {
                // Skip if already processed
                if (card.hasAttribute('data-clickable-enhanced')) return;
                card.setAttribute('data-clickable-enhanced', 'true');
                
                // Add click handler to the entire card (non-intrusive)
                card.addEventListener('click', function(e) {
                    // Only handle clicks that aren't on the radio button or label
                    const target = e.target;
                    const radioButton = card.querySelector('input[type="radio"]');
                    const label = card.querySelector('label');
                    
                    // If click is already on radio button or label, let it handle naturally
                    if (target === radioButton || target === label || 
                        target.closest('label') || target.closest('input[type="radio"]')) {
                        return; // Let APO handle this naturally
                    }
                    
                    // Only intercept clicks on empty areas of the card
                    if (radioButton && !radioButton.checked) {
                        // Simply click the radio button naturally - no event manipulation
                        radioButton.click();
                    }
                }, { passive: true }); // Use passive listener
            });
        } catch (error) {
            // Silent error handling
        }
    }

    // Function to make entire checkbox card clickable (Non-intrusive approach)
    function makeCheckboxCardsClickable() {
        try {
            // ONLY target checkbox cards inside .mw-groups container
            const checkboxCards = document.querySelectorAll('.mw-groups .mw-option--type-checkbox .mw-option__value');
            
            checkboxCards.forEach(card => {
                // Skip if already processed
                if (card.hasAttribute('data-clickable-enhanced')) return;
                card.setAttribute('data-clickable-enhanced', 'true');
                
                // Add click handler to the entire card (non-intrusive)
                card.addEventListener('click', function(e) {
                    // Only handle clicks that aren't on the checkbox or label
                    const target = e.target;
                    const checkboxButton = card.querySelector('input[type="checkbox"]');
                    const label = card.querySelector('label');
                    
                    // If click is already on checkbox or label, let it handle naturally
                    if (target === checkboxButton || target === label || 
                        target.closest('label') || target.closest('input[type="checkbox"]')) {
                        return; // Let APO handle this naturally
                    }
                    
                    // Only intercept clicks on empty areas of the card - toggle checkbox
                    if (checkboxButton) {
                        // Simply click the checkbox naturally - no event manipulation
                        checkboxButton.click();
                    }
                }, { passive: true }); // Use passive listener
            });
        } catch (error) {
            // Silent error handling
        }
    }

    /* ===== SECTION 2: RADIO BUTTON IMAGE LAYOUT SYSTEM ===== */
    /* Enhanced layout for product option images with responsive design */
    
    // Configuration for image layout
    const LAYOUT_OPTIONS = {
        imagePosition: 'left', // 'left', 'right', 'top', 'bottom'
        imageSize: 'medium',   // 'small', 'medium', 'large'
        layoutStyle: 'card',   // 'card', 'minimal', 'showcase', 'grid'
        spacing: 'normal'      // 'tight', 'normal', 'loose'
    };
    
    const IMAGE_SIZES = {
        small: { width: '50px', height: '50px' },
        medium: { width: '80px', height: '80px' },
        large: { width: '120px', height: '120px' }
    };
    
    const SPACING_VALUES = {
        tight: '8px',
        normal: '15px',
        loose: '25px'
    };
    
    // Function to apply radio image layout
    
    // ===== SYSTEM 5: IMAGE LAYOUT OPTIMIZER =====
    // Dynamically repositions images in radio/checkbox cards for better visual density
    // Creates CSS for configurable image sizing and positioning (left, right, top, bottom)
    
    // Function to apply radio image layout
    function applyRadioImageLayout() {
        try {
            // ONLY target radio containers inside .mw-groups container
            const allRadioContainers = document.querySelectorAll('.mw-groups .mw-option--type-radio .mw-option__value');
            
            allRadioContainers.forEach(option => {
                const media = option.querySelector('.mw-option__media, .option-media, .product-image, img');
                const hasRadio = option.querySelector('input[type="radio"]');
                
                // Only apply to containers that have both radio and media
                if (media && hasRadio) {
                    restructureRadioLayout(option, media);
                    applyLayoutToOption(option, media);
                }
            });
            
        } catch (error) {
            // Silent error handling
        }
    }

    // Function to apply checkbox image layout
    function applyCheckboxImageLayout() {
        try {
            // ONLY target checkbox containers inside .mw-groups container
            const allCheckboxContainers = document.querySelectorAll('.mw-groups .mw-option--type-checkbox .mw-option__value');
            
            allCheckboxContainers.forEach(option => {
                const media = option.querySelector('.mw-option__media, .option-media, .product-image, img');
                const hasCheckbox = option.querySelector('input[type="checkbox"]');
                
                // Only apply to containers that have both checkbox and media
                if (media && hasCheckbox) {
                    restructureCheckboxLayout(option, media);
                    applyLayoutToCheckboxOption(option, media);
                }
            });
            
        } catch (error) {
            // Silent error handling
        }
    }
    
    // Unified restructure function for both radio and checkbox layouts
    function restructureLayout(option, media, isCheckbox = false) {
        try {
            if (option.dataset.layoutRestructured === 'true') return;
            
            const mainStack = option.querySelector('.mw-stack--vertical');
            if (!mainStack) return;
            
            const stackItems = Array.from(mainStack.querySelectorAll('.mw-stack__item'));
            const labelItem = stackItems.find(item => item.querySelector('label'));
            const descriptionItem = stackItems.find(item => 
                !item.querySelector('label, .mw-option__media') && item.textContent.trim().length > 20
            );
            
            if (media && mainStack.contains(media)) option.insertBefore(media, mainStack);
            
            mainStack.style.order = '2';
            if (media) media.style.order = '1';
            
            if (descriptionItem) {
                descriptionItem.style.paddingLeft = '0';
                descriptionItem.style.lineHeight = '1.2';
                if (isCheckbox) descriptionItem.style.paddingRight = '40px';
            }
            
            if (labelItem && !descriptionItem) {
                Object.assign(labelItem.style, { width: '100%', display: 'flex', alignItems: 'center' });
            }
            
            option.dataset.layoutRestructured = 'true';
        } catch (error) {}
    }
    
    // Backward compatibility wrappers
    function restructureRadioLayout(option, media) { restructureLayout(option, media, false); }
    function restructureCheckboxLayout(option, media) { restructureLayout(option, media, true); }
    
    function applyLayoutToOption(option, media) {
        // Apply base layout classes
        option.classList.add('radio-enhanced-layout');
        
        // Force proper two-column layout: image (25%) + content (75%)
        option.style.display = 'flex';
        option.style.alignItems = 'stretch';
        option.style.flexDirection = 'row';
        option.style.padding = '16px 20px';
        option.style.marginBottom = '12px';
        option.style.gap = '16px';
        option.style.border = '2px solid #e5e7eb';
        option.style.borderRadius = '12px';
        option.style.backgroundColor = 'white';
        option.style.cursor = 'pointer';
        option.style.transition = 'all 0.3s ease';
        option.style.minHeight = '80px';
        
        // Set image to 25% width with larger size and no borders
        if (media) {
            media.style.width = '25%';
            media.style.maxWidth = '120px';
            media.style.minWidth = '80px';
            media.style.height = '100px';
            media.style.flexShrink = '0';
            media.style.order = '1';
            media.style.margin = '0';
            media.style.borderRadius = '8px';
            media.style.overflow = 'hidden';
            media.style.background = 'transparent';
            media.style.border = 'none';
            media.style.display = 'flex';
            media.style.alignItems = 'center';
            media.style.justifyContent = 'center';
            media.style.padding = '0';
            
            // Style the img inside media - full size
            const img = media.querySelector('img');
            if (img) {
                img.style.width = '100%';
                img.style.height = '100%';
                img.style.objectFit = 'cover';
                img.style.borderRadius = '8px';
                img.style.display = 'block';
            }
        }
        
        // Batch apply content area styles
        const contentArea = option.querySelector('.mw-stack--vertical, .option-content') || option;
        
        if (contentArea && contentArea !== option) {
            Object.assign(contentArea.style, {
                order: '2',
                flex: '1',
                width: '75%',
                minWidth: '0',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                margin: '0'
            });
        }
        
        // Batch apply label styles
        const label = option.querySelector('label');
        if (label) {
            Object.assign(label.style, {
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0',
                margin: '0',
                width: '100%',
                cursor: 'pointer',
                flexDirection: 'row',
                marginBottom: '8px'
            });
        }
    }

    // Unified layout function for checkbox (radio handled above)
    function applyLayoutToCheckboxOption(option, media) {
        option.classList.add('checkbox-enhanced-layout');
        
        Object.assign(option.style, {
            display: 'flex', alignItems: 'stretch', flexDirection: 'row', padding: '16px 20px',
            marginBottom: '12px', gap: '16px', border: '2px solid #e5e7eb', borderRadius: '12px',
            backgroundColor: 'white', cursor: 'pointer', transition: 'all 0.3s ease', minHeight: '80px'
        });
        
        if (media) {
            Object.assign(media.style, {
                width: '25%', maxWidth: '120px', minWidth: '80px', height: '100px', flexShrink: '0',
                order: '1', margin: '0', borderRadius: '8px', overflow: 'hidden', background: 'transparent',
                border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0'
            });
            const img = media.querySelector('img');
            if (img) Object.assign(img.style, { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', display: 'block' });
        }
        
        const contentArea = option.querySelector('.mw-stack--vertical, .option-content') || option;
        if (contentArea && contentArea !== option) {
            Object.assign(contentArea.style, { order: '2', flex: '1', width: '75%', minWidth: '0', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', margin: '0' });
        }
        
        const label = option.querySelector('label');
        if (label) Object.assign(label.style, { display: 'flex', alignItems: 'center', gap: '0', margin: '0', width: '100%', cursor: 'pointer', flexDirection: 'row', marginBottom: '8px', paddingRight: '40px' });

        const checkbox = option.querySelector('input[type="checkbox"]');
        if (checkbox) Object.assign(checkbox.style, { display: 'block', width: '18px', height: '18px', margin: '0', position: 'absolute', right: '20px', top: '50%', transform: 'translateY(-50%)', flexShrink: '0', accentColor: '#000000', cursor: 'pointer', zIndex: '10' });
    }

    // Function to create dynamic CSS for radio image layout
    function createRadioImageCSS() {
        const style = document.createElement('style');
        style.textContent = `
            /* Universal Radio Button Layout - ONLY inside mw-groups container */
            .mw-groups .radio-enhanced-layout,
            .mw-groups .mw-option__value,
            .mw-groups .radio-option,
            .mw-groups .option-value {
                display: flex !important;
                align-items: center !important;
                padding: 1px !important;
                border: 2px solid transparent !important;
                border-radius: 10px !important;
                margin-bottom: 10px !important;
                background: white !important;
                cursor: pointer !important;
                transition: border-color 0.2s ease !important;
                position: relative !important;
                min-height: 60px !important;
                width: 100% !important;
                box-sizing: border-box !important;
                flex-wrap: nowrap !important;
                overflow: hidden !important;
            }
            
            /* Universal Image styling - prevent cropping - ONLY inside mw-groups */
            .mw-groups .radio-enhanced-layout .mw-option__media,
            .mw-groups .radio-enhanced-layout .mw-option__media-image,
            .mw-groups .radio-enhanced-layout .option-media,
            .mw-groups .radio-enhanced-layout .product-image,
            .mw-groups .radio-enhanced-layout img,
            .mw-groups .mw-option__value .mw-option__media,
            .mw-groups .mw-option__value .mw-option__media-image,
            .mw-groups .mw-option__value img {
                width: 120px !important;
                height: 80px !important;
                border: none !important;
                border-radius: 8px !important;
                overflow: visible !important;
                margin: 0 !important;
                margin-left: 5px !important;
                margin-right: 15px !important;
                flex-shrink: 0 !important;
                order: 1 !important;
                object-fit: contain !important;
                background: transparent !important;
                padding: 5px !important;
            }
            
            .mw-groups .radio-enhanced-layout .mw-option__media-image img,
            .mw-groups .mw-option__value .mw-option__media-image img {
                width: 100% !important;
                height: 100% !important;
                object-fit: contain !important;
                display: block !important;
                border: none !important;
                background: transparent !important;
                max-width: none !important;
                max-height: none !important;
            }
            
            /* Universal Content styling - prevent text wrapping - ONLY mw-groups */
            .mw-groups .radio-enhanced-layout .mw-stack--vertical,
            .mw-groups .radio-enhanced-layout .option-content,
            .mw-groups .mw-option__value .mw-stack--vertical {
                flex: 1 !important;
                margin: 0 !important;
                display: flex !important;
                flex-direction: row !important;
                align-items: center !important;
                order: 2 !important;
                min-width: 0 !important;
                overflow: hidden !important;
            }
            
            .mw-groups .radio-enhanced-layout .mw-stack__item,
            .mw-groups .mw-option__value .mw-stack__item {
                margin: 0 !important;
                display: flex !important;
                align-items: center !important;
            }
            
            .mw-groups .radio-enhanced-layout .mw-label,
            .mw-groups .radio-enhanced-layout label,
            .mw-groups .mw-option__value .mw-label,
            .mw-groups .mw-option__value label {
                display: flex !important;
                align-items: center !important;
                gap: 12px !important;
                margin: 0 !important;
                width: 100% !important;
                cursor: pointer !important;
            }
            
            .mw-groups .radio-enhanced-layout .mw-option__control[type="radio"],
            .mw-groups .radio-enhanced-layout input[type="radio"],
            .mw-groups .mw-option__value input[type="radio"] {
                width: 18px !important;
                height: 18px !important;
                margin: 0 !important;
                accent-color: #007cba !important;
                flex-shrink: 0 !important;
            }
            
            .mw-groups .radio-enhanced-layout .mw-option__value-text,
            .mw-groups .radio-enhanced-layout .option-text,
            .mw-groups .mw-option__value .mw-option__value-text {
                font-weight: 500 !important;
                color: #333 !important;
                line-height: 1.4 !important;
                flex: 1 !important;
                overflow: hidden !important;
                text-overflow: ellipsis !important;
                word-wrap: break-word !important;
                hyphens: auto !important;
                min-width: 0 !important;
            }
            
            .mw-groups .radio-enhanced-layout .mw-option__price,
            .mw-groups .radio-enhanced-layout .option-price,
            .mw-groups .mw-option__value .mw-option__price {
                color: #007cba !important;
                font-weight: 600 !important;
                margin-left: 8px !important;
            }
            
            /* Mobile responsive - ONLY mw-groups */
            @media (max-width: 768px) {
                .mw-groups .radio-enhanced-layout,
                .mw-groups .mw-option__value {
                    padding: 12px !important;
                    min-height: 60px !important;
                    flex-direction: row !important;
                    align-items: flex-start !important;
                }
                
                .mw-groups .radio-enhanced-layout .mw-option__media,
                .mw-groups .radio-enhanced-layout img,
                .mw-groups .mw-option__value .mw-option__media,
                .mw-groups .mw-option__value img {
                    width: 45px !important;
                    height: 45px !important;
                    margin-left: 5px !important;
                    margin-right: 10px !important;
                    flex-shrink: 0 !important;
                    margin-top: 2px !important;
                }
                
                .mw-groups .radio-enhanced-layout .mw-option__value-text,
                .mw-groups .mw-option__value .mw-option__value-text {
                    font-size: 13px !important;
                    line-height: 1.3 !important;
                    white-space: normal !important;
                    word-wrap: break-word !important;
                    overflow-wrap: break-word !important;
                    hyphens: auto !important;
                }
                
                .mw-groups .radio-enhanced-layout .mw-stack--vertical,
                .mw-groups .mw-option__value .mw-stack--vertical {
                    flex-direction: column !important;
                    align-items: flex-start !important;
                    gap: 4px !important;
                }
                
                .mw-groups .radio-enhanced-layout .mw-label,
                .mw-groups .mw-option__value .mw-label {
                    flex-direction: row !important;
                    align-items: flex-start !important;
                    gap: 8px !important;
                }
                
                .mw-groups .radio-enhanced-layout input[type="radio"],
                .mw-groups .mw-option__value input[type="radio"] {
                    width: 16px !important;
                    height: 16px !important;
                    margin-top: 2px !important;
                    margin-right: 8px !important;
                }
            }
            
            /* Extra small screens - ONLY mw-groups */
            @media (max-width: 480px) {
                .mw-groups .radio-enhanced-layout,
                .mw-groups .mw-option__value {
                    padding: 1px !important;
                    margin-bottom: 8px !important;
                    min-height: 50px !important;
                }
                
                .mw-groups .radio-enhanced-layout .mw-option__media,
                .mw-groups .radio-enhanced-layout img,
                .mw-groups .mw-option__value .mw-option__media,
                .mw-groups .mw-option__value img {
                    width: 120px !important;
                    height: 80px !important;
                    margin-left: 2px !important;
                    margin-right: 8px !important;
                }
                
                .mw-groups .radio-enhanced-layout .mw-option__value-text,
                .mw-groups .mw-option__value .mw-option__value-text {
                    font-size: 12px !important;
                    line-height: 1.2 !important;
                }
                
                .mw-groups .radio-enhanced-layout input[type="radio"],
                .mw-groups .mw-option__value input[type="radio"] {
                    width: 14px !important;
                    height: 14px !important;
                    margin-top: 1px !important;
                    margin-right: 6px !important;
                }
            }
        `;
        document.head.appendChild(style);
    }
    
    // Function to enable different layout presets
    function setLayoutPreset(preset) {
        switch (preset) {
            case 'modern':
                LAYOUT_OPTIONS.imagePosition = 'left';
                LAYOUT_OPTIONS.imageSize = 'medium';
                LAYOUT_OPTIONS.layoutStyle = 'card';
                LAYOUT_OPTIONS.spacing = 'normal';
                break;
                
            case 'compact':
                LAYOUT_OPTIONS.imagePosition = 'left';
                LAYOUT_OPTIONS.imageSize = 'small';
                LAYOUT_OPTIONS.layoutStyle = 'minimal';
                LAYOUT_OPTIONS.spacing = 'tight';
                break;
                
            case 'showcase':
                LAYOUT_OPTIONS.imagePosition = 'left';
                LAYOUT_OPTIONS.imageSize = 'large';
                LAYOUT_OPTIONS.layoutStyle = 'showcase';
                LAYOUT_OPTIONS.spacing = 'loose';
                break;
                
            case 'grid':
                LAYOUT_OPTIONS.imagePosition = 'top';
                LAYOUT_OPTIONS.imageSize = 'medium';
                LAYOUT_OPTIONS.layoutStyle = 'grid';
                LAYOUT_OPTIONS.spacing = 'normal';
                break;
        }
    }
    
    /* ===== SECTION 3: COMBINED INITIALIZATION SYSTEM ===== */
    /* Orchestrates all components together */
    
    // Optimized continuous monitoring with reduced frequency
    function createContinuousMonitor() {
        // ===== FIX: Clear any existing interval before starting a new one =====
        // Previously, every APO re-render (e.g. color swatch switch) triggered
        // createContinuousMonitor() again, creating multiple parallel setIntervals.
        // These would all run verifyAndFix() simultaneously, causing layout conflicts
        // (images going vertical, cards styled over each other).
        if (continuousMonitorInterval) {
            clearInterval(continuousMonitorInterval);
            continuousMonitorInterval = null;
        }
        
        let checksCompleted = 0;
        
        function verifyAndFix() {
            try {
                checksCompleted++;
                let needsWork = false;
                
                // Combined check for radio and checkbox cards needing layout
                const radioCards = document.querySelectorAll('.mw-groups .mw-option--type-radio .mw-option__value');
                const checkboxCards = document.querySelectorAll('.mw-groups .mw-option--type-checkbox .mw-option__value');
                
                // Batch check radio cards
                radioCards.forEach(card => {
                    if (!card.style.display || card.style.display !== 'flex') {
                        const media = card.querySelector('.mw-option__media');
                        if (media) {
                            applyLayoutToOption(card, media);
                            needsWork = true;
                        }
                    }
                });
                
                // Batch check checkbox cards
                checkboxCards.forEach(card => {
                    const checkbox = card.querySelector('input[type="checkbox"]');
                    if (checkbox && (!checkbox.style.position || checkbox.style.position !== 'absolute')) {
                        const media = card.querySelector('.mw-option__media');
                        if (media) {
                            applyLayoutToCheckboxOption(card, media);
                            needsWork = true;
                        }
                    }
                });
                
                // Check prescription toggle only if detected
                if (detectedRadios && !document.querySelector('.mw-group__progress.show-progress')) {
                    const checkedRadio = document.querySelector(`.mw-groups input[name="${detectedRadios.groupName}"]:checked`);
                    if (checkedRadio) {
                        updateRadioSelectionStates(checkedRadio);
                    }
                }
                
                // Check clickability for both types
                const unclickableRadios = document.querySelectorAll('.mw-groups .mw-option--type-radio .mw-option__value:not([data-clickable-enhanced])');
                const unclickableCheckboxes = document.querySelectorAll('.mw-groups .mw-option--type-checkbox .mw-option__value:not([data-clickable-enhanced])');
                
                if (unclickableRadios.length > 0) {
                    makeRadioCardsClickable();
                    needsWork = true;
                }
                
                if (unclickableCheckboxes.length > 0) {
                    makeCheckboxCardsClickable();
                    needsWork = true;
                }
                
                // Check form steps
                const unmarkedFormSteps = document.querySelectorAll('.prescription-multistep .mw-group__options:not(.form-step-detected)');
                if (unmarkedFormSteps.length > 0) {
                    detectAndMarkFormSteps();
                    needsWork = true;
                }
                
                // Stop monitoring early if everything is stable for 3 consecutive checks
                if (!needsWork && checksCompleted >= 3) {
                    clearInterval(continuousMonitorInterval);
                    continuousMonitorInterval = null;
                }
                
            } catch (error) {
                // Silent error handling
            }
        }
        
        // Check every 3 seconds
        continuousMonitorInterval = setInterval(verifyAndFix, 3000);
        
        // Stop monitoring after 24 seconds (8 checks max)
        setTimeout(() => {
            if (continuousMonitorInterval) {
                clearInterval(continuousMonitorInterval);
                continuousMonitorInterval = null;
            }
        }, 24000);
    }
    
    // Function to run the combined detection and setup
    async function runCombinedSystem() {
        try {
            // Wait for essential APO elements to be present - specifically .mw-groups
            const requiredSelectors = [
                '.mw-groups',
                '.mw-groups .mw-option__value',
                '.mw-group__container'
            ];
            
            await waitForElements(requiredSelectors, 15000);
            
            // Add additional delay to ensure APO styling is complete
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // 1. Detect and classify prescription fields
            detectPrescriptionFields();
            
            // 2. Detect and mark form steps dynamically (for auto-height)
            detectAndMarkFormSteps();
            
            // 3. Apply fallback styling for older browsers
            applyFallbackStyling();
            
            // 4. Initialize radio image layout system
            setLayoutPreset('modern');
            createRadioImageCSS();
            
            // Wait for radio/checkbox elements specifically inside .mw-groups
            const inputSelectors = [
                '.mw-groups input[type="radio"]',
                '.mw-groups .mw-option--type-radio'
            ];
            
            try {
                await waitForElements(inputSelectors, 5000);
            } catch (e) {
                // Continue even if radio elements not found
            }
            
            applyRadioImageLayout();

            // 4. Initialize checkbox image layout system
            applyCheckboxImageLayout();
            
            // 5. Initialize universal prescription toggle system (with verification)
            setTimeout(() => {
                if (document.querySelector('.mw-group__progress')) {
                    universalToggle();
                }
            }, 500);
            
            // 6. Initialize radio button selection states for all radio groups (with verification)
            setTimeout(() => {
                if (document.querySelectorAll('.mw-groups input[type="radio"]').length > 0) {
                    initializeAllRadioStates();
                }
            }, 1000);
            
            // 7. Make radio and checkbox cards fully clickable (with verification)
            setTimeout(() => {
                makeRadioCardsClickable();
                makeCheckboxCardsClickable();
            }, 1500);
            
            // 8. Start continuous monitoring
            createContinuousMonitor();
            
        } catch (error) {
            // Fallback: try again with longer delay
            setTimeout(() => {
                try {
                    // Simplified fallback run
                    detectPrescriptionFields();
                    applyFallbackStyling();
                    setLayoutPreset('modern');
                    createRadioImageCSS();
                    applyRadioImageLayout();
                    applyCheckboxImageLayout();
                    
                    setTimeout(universalToggle, 500);
                    setTimeout(initializeAllRadioStates, 1000);
                    setTimeout(() => {
                        makeRadioCardsClickable();
                        makeCheckboxCardsClickable();
                    }, 1500);
                    
                    createContinuousMonitor();
                } catch (e) {
                    // Silent error handling
                }
            }, 3000);
        }
    }
    
    // Function to initialize selection states for all radio buttons (Ultra-passive)
    function initializeAllRadioStates() {
        try {
            // ONLY get radio buttons inside .mw-groups container
            const allRadios = document.querySelectorAll('.mw-groups input[type="radio"]');
            const processedGroups = new Set();
            
            allRadios.forEach(radio => {
                const groupName = radio.name;
                
                // Skip if we've already processed this group
                if (processedGroups.has(groupName)) return;
                processedGroups.add(groupName);
                
                // Add ultra-passive event listeners for this group (only visual updates)
                const groupRadios = document.querySelectorAll(`.mw-groups input[name="${groupName}"]`);
                groupRadios.forEach(groupRadio => {
                    // Skip prescription radio group to avoid conflicts
                    if (detectedRadios && groupName === detectedRadios.groupName) {
                        return; // Let universalToggle handle this group
                    }
                    
                    // Use passive listener only for visual state updates
                    groupRadio.addEventListener('change', function() {
                        setTimeout(() => updateRadioSelectionStates(this), 300);
                    }, { passive: true });
                    
                    // Initialize current state with longer delay
                    if (groupRadio.checked) {
                        setTimeout(() => updateRadioSelectionStates(groupRadio), 500);
                    }
                });
            });
        } catch (error) {
            // Silent error handling
        }
    }
    
    // Optimized intelligent initialization with retry logic
    let initAttempts = 0;
    const maxAttempts = 3;
    
    function intelligentInit() {
        initAttempts++;
        
        // Check if required elements exist
        const hasRequired = document.querySelector('.mw-groups');
        
        if (hasRequired || initAttempts >= maxAttempts) {
            runCombinedSystem();
        } else {
            // Retry with exponential backoff
            setTimeout(intelligentInit, 1000 * initAttempts);
        }
    }
    
    // Single initialization based on document state
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => setTimeout(intelligentInit, 100));
    } else if (document.readyState === 'interactive') {
        setTimeout(intelligentInit, 100);
    } else {
        intelligentInit();
    }

    // ===== FIX: Lightweight re-init on APO re-render (e.g. color swatch switch) =====
    // Previously, every re-render triggered the full runCombinedSystem() which:
    //   1. Waited up to 15 seconds for elements (waitForElements polling)
    //   2. Added an extra 1000ms delay before running
    //   3. Set detectedRadios = null immediately, crashing any in-flight change events
    //   4. Left cards unclickable for ~1.5+ seconds
    // Now: cards are made clickable immediately, prescription detection re-runs fast,
    // and the full system only re-runs if the radio detection group is actually lost.
    const debouncedRerun = debounce(() => {
        try {
            // Step 1: Immediately restore card clickability (most visible symptom)
            makeRadioCardsClickable();
            makeCheckboxCardsClickable();
            
            // Step 2: Re-detect and apply prescription layout classes
            detectPrescriptionFields();
            detectAndMarkFormSteps();
            
            // Step 3: Re-apply image layout to any new cards
            applyRadioImageLayout();
            applyCheckboxImageLayout();
            
            // Step 4: Re-check if our prescription radio group still exists in the DOM.
            // If the group is gone (APO fully rebuilt), we need to re-detect it.
            const groupStillExists = detectedRadios && 
                document.querySelector(`.mw-groups input[name="${detectedRadios.groupName}"]`);
            
            if (!groupStillExists) {
                // Radio group was replaced — need full re-detection
                detectedRadios = null;
                // Run the full system but without the extra 1000ms idle delay
                runCombinedSystem();
            } else {
                // Group still exists — just refresh visual states
                setTimeout(() => {
                    initializeAllRadioStates();
                    if (document.querySelector('.mw-group__progress')) {
                        universalToggle();
                    }
                }, 200);
            }
        } catch (e) {
            // Fallback to full re-run if lightweight path fails
            detectedRadios = null;
            runCombinedSystem();
        }
    }, 300);
    
    // ===== FIX: Broadened MutationObserver =====
    // Previous bug: observer ONLY fired when a '.mw-groups' element was ADDED to the DOM.
    // Reality: When you switch a color swatch, APO calls "Load new options by variant_id".
    // APO KEEPS the existing .mw-groups element and REPLACES its inner children
    // (the .mw-group, .mw-option__value nodes). The old observer never saw this
    // because its condition checked addedNodes for .mw-groups — which wasn't added,
    // only its children were. Result: our system never re-initialized after swatch switches.
    let observerDebounceTimer = null;
    const mainObserver = new MutationObserver(function(mutations) {
        for (const mutation of mutations) {
            if (mutation.type !== 'childList' || mutation.addedNodes.length === 0) continue;

            const target = mutation.target;

            // Case 1: APO replaces content INSIDE .mw-groups or .mw-apo-container
            // (the most common case when variant/swatch changes)
            if (target.classList &&
                (target.classList.contains('mw-groups') ||
                 target.classList.contains('mw-apo-container') ||
                 target.classList.contains('mw-group') ||
                 target.classList.contains('mw-group__options'))) {
                clearTimeout(observerDebounceTimer);
                observerDebounceTimer = setTimeout(debouncedRerun, 100);
                return;
            }

            // Case 2: .mw-groups or an ancestor of it is added fresh to the DOM
            for (const node of mutation.addedNodes) {
                if (node.nodeType !== 1) continue;
                if (node.classList &&
                    (node.classList.contains('mw-groups') ||
                     node.classList.contains('mw-apo-container'))) {
                    clearTimeout(observerDebounceTimer);
                    observerDebounceTimer = setTimeout(debouncedRerun, 100);
                    return;
                }
                // Case 3: A container wrapping .mw-groups is injected (e.g. theme section)
                if (node.querySelector && node.querySelector('.mw-groups')) {
                    clearTimeout(observerDebounceTimer);
                    observerDebounceTimer = setTimeout(debouncedRerun, 100);
                    return;
                }
            }
        }
    });

    // Observe document.body to catch all APO DOM changes
    mainObserver.observe(document.body, {
        childList: true,
        subtree: true
    });    /* ===== SECTION 4: PUBLIC API ===== */
    /* Export functions for manual control and debugging */
    
    window.combinedSystem = {
        // Prescription system functions
        setupPrescription: universalToggle,
        detectRadios: detectRadioStructure,
        detectFields: detectPrescriptionFields,
        getCurrentStep: () => currentStep,
        getDetectedRadios: () => detectedRadios,
        patterns: FIELD_PATTERNS,
        
        // Radio image layout functions
        applyImageLayout: applyRadioImageLayout,
        setPreset: setLayoutPreset,
        setOptions: (options) => Object.assign(LAYOUT_OPTIONS, options),
        
        // Checkbox image layout functions
        applyCheckboxLayout: applyCheckboxImageLayout,
        
        // Radio card interaction
        makeCardsClickable: makeRadioCardsClickable,
        
        // Checkbox card interaction
        makeCheckboxCardsClickable: makeCheckboxCardsClickable,
        
        // Combined system functions
        runAll: runCombinedSystem,
        version: '1.0.1'
    };
    
})();